import {
    getGetParcelsByClientIdUrl,
    getGetParcelsUrl
} from 'generatedTypes/client/services/parcel-controller';
import { useState } from 'react';
import { filterDemoRecords } from 'utils/demoRecordFilter';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { FetchFarms, FetchFarmsParams, ParcelResponse } from './types';

const useFarms = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchFarms: FetchFarms = async request => {
        const { clientId, name, pageSize, pageToken, sort } = request;

        try {
            setIsLoading(true);

            const url = clientId
                ? getGetParcelsByClientIdUrl(clientId, { name, pageSize, pageToken, sort })
                : getGetParcelsUrl({ pageSize, pageToken, sort });

            const response = await withRetry(
                () => authenticatedGet(url),
                CONNECTION_ERROR,
                () => fetchFarms(request)
            );

            if (response && response.objects) {
                return { ...response, objects: filterDemoRecords(response.objects) };
            }

            return response;
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: farmsData,
        fetch: paginatedFetch,
        reset,
        totalCount
    } = usePagination<ParcelResponse>({
        callback: fetchFarms
    });

    const handleFetch = (args?: FetchFarmsParams) => {
        const { clientId, name, pageSize = DEFAULT_PAGE_SIZE } = args || {};

        return paginatedFetch({
            clientId,
            name,
            pageSize
        });
    };

    return {
        farms: farmsData as ParcelResponse[],
        fetch: handleFetch,
        isLoading,
        reset,
        totalCount
    };
};

export default useFarms;
